// 问卷操作自定义Hook
// 封装问卷相关的API调用和状态管理

import React, { useState, useCallback, useEffect } from 'react';
import { message, Modal } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../store';
import {
  useCreateQuestionnaireMutation,
  useUpdateQuestionnaireMutation,
  useSubmitQuestionnaireMutation,
  useGetQuestionnaireByIdQuery,
  // useSaveQuestionnaireDraftMutation, // 不再需要，使用本地存储
  useGetQuestionnaireAIScoreMutation,
} from '../store/api/questionnaireApi';
import {
  InitialScreeningQuestionnaire,
  QuestionnaireRequest,
  QuestionnaireStatus,
  DEFAULT_QUESTIONNAIRE,
} from '../types/questionnaire';

interface UseQuestionnaireOptions {
  questionnaireId?: string;
  autoSave?: boolean;
  autoSaveInterval?: number; // 自动保存间隔（毫秒）
}

export const useQuestionnaire = (options: UseQuestionnaireOptions = {}) => {
  const {
    questionnaireId: initialQuestionnaireId,
    autoSave = true,
    autoSaveInterval = 30000,
  } = options;

  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const resolveLocalizedMessage = useCallback(
    (backendMessage: string | undefined, fallbackKey: string) => {
      if (!backendMessage) return t(fallbackKey);
      const isZh = i18n.language.toLowerCase().startsWith('zh');
      const hasChinese = /[\u4e00-\u9fff]/.test(backendMessage);
      return !isZh && hasChinese ? t(fallbackKey) : backendMessage;
    },
    [i18n.language, t]
  );

  // 获取认证状态
  const { isAuthenticated } = useAppSelector(state => state.auth);

  // 检查编辑模式和重新提交模式
  const editMode = location.state?.editMode || false;
  const resubmitMode = location.state?.resubmitMode || false;
  const editQuestionnaireId = location.state?.questionnaireId;

  // 本地状态管理 - 优先使用编辑模式的问卷ID
  const [questionnaireId, setQuestionnaireId] = useState<string | null>(
    editMode && editQuestionnaireId
      ? editQuestionnaireId
      : initialQuestionnaireId || null
  );

  // RTK Query hooks
  const [createQuestionnaire, { isLoading: isCreating }] =
    useCreateQuestionnaireMutation();
  const [updateQuestionnaire, { isLoading: isUpdating }] =
    useUpdateQuestionnaireMutation();
  const [submitQuestionnaire, { isLoading: isSubmitting }] =
    useSubmitQuestionnaireMutation();
  // const [saveDraft, { isLoading: isSavingDraft }] = useSaveQuestionnaireDraftMutation(); // 不再需要，使用本地存储
  // 编辑模式下使用 API 保存，需要显示加载状态；新建模式下使用 localStorage，无加载状态
  const [getAIScore, { isLoading: isCalculatingScore }] =
    useGetQuestionnaireAIScoreMutation();

  // 获取问卷详情 - 只在用户已认证且有问卷ID的情况下查询
  const {
    data: questionnaireData,
    isLoading: isLoadingQuestionnaire,
    refetch: refetchQuestionnaire,
  } = useGetQuestionnaireByIdQuery(questionnaireId!, {
    skip: !questionnaireId || !isAuthenticated, // 未认证时跳过查询
  });

  // 本地状态 - 初始状态为空，等待草稿加载
  const [localQuestionnaire, setLocalQuestionnaire] = useState<
    Partial<InitialScreeningQuestionnaire>
  >({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [aiScore, setAiScore] = useState<number | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({});

  // 编辑模式初始化 - 处理编辑模式下的数据加载
  useEffect(() => {
    if (editMode && editQuestionnaireId && isAuthenticated) {
      console.log('编辑模式初始化，问卷ID:', editQuestionnaireId);
      console.log('重新提交模式:', resubmitMode);
      console.log('认证状态:', isAuthenticated);

      // 设置问卷ID以触发API查询
      setQuestionnaireId(editQuestionnaireId);

      // 强制刷新数据，避免使用缓存
      // 延迟执行确保 questionnaireId 已设置
      setTimeout(() => {
        if (refetchQuestionnaire) {
          console.log('强制刷新问卷数据');
          refetchQuestionnaire();
        }
      }, 100);
    } else if (editMode && editQuestionnaireId && !isAuthenticated) {
      console.log('编辑模式但用户未认证，跳转到登录页');
      // 保存要编辑的问卷ID，登录后可以继续
      localStorage.setItem(
        'pendingQuestionnaireEdit',
        JSON.stringify({
          questionnaireId: editQuestionnaireId,
          editMode,
          resubmitMode,
        })
      );
      navigate('/auth/login');
    }
  }, [
    editMode,
    editQuestionnaireId,
    resubmitMode,
    isAuthenticated,
    navigate,
    refetchQuestionnaire,
  ]);

  // 同步远程数据到本地状态
  useEffect(() => {
    if (questionnaireData?.success && questionnaireData.data.questionnaire) {
      console.log('加载问卷数据到表单:', questionnaireData.data.questionnaire);
      setLocalQuestionnaire(questionnaireData.data.questionnaire);
      setHasUnsavedChanges(false);
      setLastSavedAt(
        questionnaireData.data.questionnaire.updatedAt
          ? new Date(questionnaireData.data.questionnaire.updatedAt)
          : new Date()
      );

      // 在编辑模式下显示提示
      if (editMode) {
        if (resubmitMode) {
          message.info('已加载问卷数据，您可以根据审核意见修改后重新提交');
        } else {
          message.info('已加载问卷数据，您可以继续编辑');
        }
      }
    }
  }, [questionnaireData, editMode, resubmitMode]);

  // 更新问卷数据
  const updateQuestionnaireData = useCallback(
    (updates: Partial<InitialScreeningQuestionnaire>) => {
      setLocalQuestionnaire(prev => ({ ...prev, ...updates }));
      setHasUnsavedChanges(true);
      setValidationErrors({}); // 清除验证错误
    },
    []
  );

  // 保存草稿 - 编辑模式使用API，新建模式使用本地存储
  const handleSaveDraft = useCallback(async () => {
    try {
      // 判断是否为编辑模式（有真实的问卷ID，不是本地草稿ID）
      const isEditingExistingQuestionnaire =
        questionnaireId && !questionnaireId.startsWith('draft_');

      if (isEditingExistingQuestionnaire || editMode) {
        // 编辑模式：使用API保存到数据库
        console.log('编辑模式 - 保存问卷到数据库:', questionnaireId);

        const { _id, createdAt, updatedAt, ...dataToUpdate } =
          localQuestionnaire;

        const result = await updateQuestionnaire({
          id: questionnaireId!,
          data: dataToUpdate as Partial<QuestionnaireRequest>,
        }).unwrap();

        // 更新本地状态
        setLocalQuestionnaire(result.data.questionnaire);
        setHasUnsavedChanges(false);
        setLastSavedAt(new Date());

        message.success(t('questionnaire:message.saveSuccess'));

        return result.data.questionnaire;
      } else {
        // 新建模式：保存到本地存储
        console.log('新建模式 - 保存草稿到本地存储:', localQuestionnaire);

        // 确保有有效的ID - 每次都生成新的唯一ID
        let draftId = questionnaireId;
        if (!draftId || !draftId.startsWith('draft_')) {
          draftId = `draft_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          setQuestionnaireId(draftId);
        }

        // 生成草稿数据
        const draftData = {
          ...localQuestionnaire,
          _id: draftId,
          status: QuestionnaireStatus.DRAFT,
          lastSavedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        // 保存到localStorage
        const storageKey = `questionnaire_draft_${draftData._id}`;
        localStorage.setItem(storageKey, JSON.stringify(draftData));

        // 更新草稿列表索引
        const draftIndex = JSON.parse(
          localStorage.getItem('questionnaire_drafts') || '[]'
        );
        if (!draftIndex.includes(draftData._id)) {
          draftIndex.push(draftData._id);
          localStorage.setItem(
            'questionnaire_drafts',
            JSON.stringify(draftIndex)
          );
        }

        // 更新本地状态 - 确保类型兼容
        setLocalQuestionnaire(
          draftData as Partial<InitialScreeningQuestionnaire>
        );
        setHasUnsavedChanges(false);
        setLastSavedAt(new Date());

        message.success(t('questionnaire:message.saveLocalSuccess'));

        return draftData;
      }
    } catch (error: any) {
      console.error('保存草稿失败:', error);
      const backendErrorMessage = error.data?.error?.message;
      message.error(
        resolveLocalizedMessage(backendErrorMessage, 'questionnaire:message.saveFailed')
      );
      throw error;
    }
  }, [
    questionnaireId,
    localQuestionnaire,
    editMode,
    updateQuestionnaire,
    resolveLocalizedMessage,
    t,
  ]);

  // 自动保存逻辑 - 使用本地存储
  useEffect(() => {
    if (!autoSave || !hasUnsavedChanges) return;

    const autoSaveTimer = setTimeout(async () => {
      try {
        await handleSaveDraft();
        console.log('自动保存成功');
      } catch (error) {
        console.error('自动保存失败:', error);
        // 自动保存失败不显示错误消息，避免打扰用户
      }
    }, autoSaveInterval);

    return () => clearTimeout(autoSaveTimer);
  }, [hasUnsavedChanges, autoSave, autoSaveInterval, handleSaveDraft]);

  // 创建新问卷
  const handleCreateQuestionnaire = useCallback(
    async (data: QuestionnaireRequest) => {
      try {
        const result = await createQuestionnaire(data).unwrap();

        message.success(result.data.message);
        setLocalQuestionnaire(result.data.questionnaire);
        setHasUnsavedChanges(false);
        setLastSavedAt(new Date());

        // 导航到编辑页面
        navigate(`/questionnaire/${result.data.questionnaire._id}/edit`);

        return result.data.questionnaire;
      } catch (error: any) {
        console.error('创建问卷失败:', error);

        // 处理已有进行中问卷的情况
        if (error.data?.error?.code === 'QUESTIONNAIRE_EXISTS') {
          const existingData = error.data?.error?.data;
          const errorMessage =
            error.data?.error?.message || '您已有进行中的问卷';
          Modal.confirm({
            title: '已有进行中的问卷',
            content: `${errorMessage}\n\n您可以查看或继续编辑已有问卷。`,
            okText: '查看已有问卷',
            cancelText: '取消',
            onOk: () => {
              if (existingData?.existingQuestionnaireId) {
                navigate('/questionnaire', {
                  state: {
                    editMode: true,
                    questionnaireId: existingData.existingQuestionnaireId,
                    from: '/admin/issuer/questionnaires',
                  },
                });
              } else {
                navigate('/admin/issuer/questionnaires');
              }
            },
          });
          return;
        }

        if (error.data?.error?.details) {
          setValidationErrors(error.data.error.details);
          message.error('表单验证失败，请检查输入内容');
        } else {
          message.error(error.data?.error?.message || '创建问卷失败');
        }

        throw error;
      }
    },
    [createQuestionnaire, navigate]
  );

  // 更新问卷
  const handleUpdateQuestionnaire = useCallback(
    async (data: Partial<QuestionnaireRequest>) => {
      if (!questionnaireId) {
        throw new Error('问卷ID不存在');
      }

      try {
        const result = await updateQuestionnaire({
          id: questionnaireId,
          data,
        }).unwrap();

        message.success(result.data.message);
        setLocalQuestionnaire(result.data.questionnaire);
        setHasUnsavedChanges(false);
        setLastSavedAt(new Date());

        return result.data.questionnaire;
      } catch (error: any) {
        console.error('更新问卷失败:', error);

        if (error.data?.error?.details) {
          setValidationErrors(error.data.error.details);
          message.error('表单验证失败，请检查输入内容');
        } else {
          message.error(error.data?.error?.message || '更新问卷失败');
        }

        throw error;
      }
    },
    [questionnaireId, updateQuestionnaire]
  );

  // 提交问卷 - 使用真实API
  const handleSubmitQuestionnaire = useCallback(async () => {
    try {
      console.log('提交问卷到后端:', localQuestionnaire);

      // 判断是否为编辑模式（有真实的问卷ID，不是本地草稿ID）
      const isEditingExistingQuestionnaire =
        questionnaireId && !questionnaireId.startsWith('draft_');

      let result: {
        success: boolean;
        data: { questionnaire: InitialScreeningQuestionnaire; message: string };
      };

      if (isEditingExistingQuestionnaire || editMode) {
        // 编辑模式：先更新问卷数据，再提交
        console.log('编辑模式 - 更新并提交问卷:', questionnaireId);

        const { _id, createdAt, updatedAt, submittedAt, ...dataToUpdate } =
          localQuestionnaire;

        // 步骤1: 更新问卷数据
        await updateQuestionnaire({
          id: questionnaireId!,
          data: dataToUpdate as Partial<QuestionnaireRequest>,
        }).unwrap();

        // 步骤2: 提交问卷
        result = await submitQuestionnaire(questionnaireId!).unwrap();
      } else {
        // 新建模式：创建并提交新问卷
        console.log('新建模式 - 创建新问卷');

        // 构建提交数据，确保包含必要字段
        // ⚠️ 重要：提交时移除draft ID，让后端生成新的唯一ID
        const { _id: draftId, ...questionnaireDataWithoutId } =
          localQuestionnaire;
        const submitData = {
          ...questionnaireDataWithoutId,
          status: QuestionnaireStatus.SUBMITTED,
        } as QuestionnaireRequest;

        // 使用真实的API创建并提交问卷
        result = await createQuestionnaire(submitData).unwrap();
      }

      // 更新本地状态为提交成功的问卷
      setLocalQuestionnaire(result.data.questionnaire);
      setHasUnsavedChanges(false);

      // 清理本地草稿（提交成功后不再需要）
      try {
        if (questionnaireId && questionnaireId.startsWith('draft_')) {
          localStorage.removeItem(`questionnaire_draft_${questionnaireId}`);
          const draftIndex = JSON.parse(
            localStorage.getItem('questionnaire_drafts') || '[]'
          );
          const updatedIndex = draftIndex.filter(
            (id: string) => id !== questionnaireId
          );
          localStorage.setItem(
            'questionnaire_drafts',
            JSON.stringify(updatedIndex)
          );
        }
      } catch (error) {
        console.error('清理草稿失败:', error);
      }

      // 显示详细的成功信息
      const aiScore = result.data.questionnaire.aiScore || 0;
      const riskLevel = result.data.questionnaire.riskLevel || 'unknown';
      const currentLocale = i18n.language.toLowerCase().startsWith('zh')
        ? 'zh-CN'
        : 'en-US';

      // 显示成功消息
      const riskLevelText =
        riskLevel === 'low'
          ? t('common:statusTag.riskLevel.low')
          : riskLevel === 'medium'
            ? t('common:statusTag.riskLevel.medium')
            : riskLevel === 'high'
              ? t('common:statusTag.riskLevel.high')
              : 'N/A';
      message.success(
        t('questionnaire:message.submitSuccessWithScore', {
          score: aiScore,
          riskLevel: riskLevelText,
        }),
        5
      );

      // 显示详细提交信息
      message.info(
        t('questionnaire:message.submitDetail', {
          id: result.data.questionnaire._id,
          submittedAt: new Date(
            result.data.questionnaire.submittedAt || Date.now()
          ).toLocaleString(currentLocale),
        }),
        8
      );
      message.info(t('questionnaire:message.submitReviewEta'), 8);

      // 智能跳转：根据用户来源决定跳转目标
      setTimeout(() => {
        // 检查用户是否已登录（通过检查token或认证状态）
        const token =
          localStorage.getItem('token') || sessionStorage.getItem('token');
        const isAuthenticated = !!token;

        // 检查是否从"我的问卷"页面进入（通过referrer或state）
        const fromMyQuestionnaires =
          location.state?.from === '/issuer/questionnaires' ||
          location.state?.from === '/admin/issuer/questionnaires' ||
          location.pathname.includes('/issuer') ||
          document.referrer.includes('/issuer/questionnaires');

        if (isAuthenticated && fromMyQuestionnaires) {
          // 已登录且从"我的问卷"进入 → 返回到"我的问卷"列表
          message.info(t('questionnaire:message.returnToList'), 2);
          navigate('/admin/issuer/questionnaires', {
            state: {
              newQuestionnaireId: result.data.questionnaire._id,
              message: t('questionnaire:message.submitSuccess'),
            },
          });
        } else if (isAuthenticated) {
          // 已登录但不是从"我的问卷"进入 → 跳转到状态查询页
          navigate('/questionnaire/status', {
            state: {
              questionnaireId: result.data.questionnaire._id,
              submittedAt: result.data.questionnaire.submittedAt,
            },
          });
        } else {
          // 未登录用户 → 建议登录查看更多功能
          message.info(t('questionnaire:message.loginSuggestion'), 3);
          navigate('/auth/login', {
            state: {
              from: '/questionnaire/status',
              questionnaireId: result.data.questionnaire._id,
              message: t('questionnaire:message.submitSuccessLoginHint'),
            },
          });
        }
      }, 3000);

      return result.data.questionnaire;
    } catch (error: any) {
      console.error('提交问卷失败:', error);
      const backendErrorMessage = error.data?.error?.message;
      message.error(
        resolveLocalizedMessage(
          backendErrorMessage,
          'questionnaire:message.submitFailed'
        )
      );
      throw error;
    }
  }, [
    localQuestionnaire,
    createQuestionnaire,
    updateQuestionnaire,
    submitQuestionnaire,
    navigate,
    editMode,
    questionnaireId,
    i18n.language,
    resolveLocalizedMessage,
    t,
  ]);

  // 计算AI评分 - 使用模拟评分
  const handleCalculateAIScore = useCallback(async () => {
    try {
      // 模拟AI评分逻辑
      console.log('模拟AI评分:', localQuestionnaire);

      // 简单的评分算法
      let score = 60; // 基础分

      // 基于填写完整度评分
      const sections = [
        'issuerBasicInfo',
        'financialStatus',
        'greenProjectInfo',
        'contactInfo',
      ];
      const completedSections = sections.filter(section => {
        const sectionData =
          localQuestionnaire[section as keyof typeof localQuestionnaire];
        return (
          sectionData &&
          typeof sectionData === 'object' &&
          Object.keys(sectionData).length > 0
        );
      });

      score += completedSections.length * 5; // 每个完成的部分+5分

      // 基于绿色项目信息额外评分
      if (localQuestionnaire.greenProjectInfo?.hasGreenProject) {
        score += 10;
      }

      // 基于企业规模评分
      if (localQuestionnaire.issuerRatingScale?.totalAssets === 'above_100b') {
        score += 10;
      }

      // 确保分数在合理范围内
      score = Math.min(100, Math.max(0, score));

      const mockResult = {
        score,
        recommendations:
          score < 70
            ? ['建议完善绿色项目信息', '建议补充财务数据']
            : ['信息完整，符合发行要求'],
      };

      setAiScore(score);

      message.success(`AI评分完成: ${score}分`);

      return mockResult;
    } catch (error: any) {
      console.error('计算AI评分失败:', error);
      message.error('计算AI评分失败');
      throw error;
    }
  }, [localQuestionnaire]);

  // 验证问卷完整性
  const validateQuestionnaire = useCallback(() => {
    const errors: Record<string, string[]> = {};

    // 基本信息验证
    if (!localQuestionnaire.issuerBasicInfo?.issuerName) {
      errors.issuerBasicInfo = ['发行人全称不能为空'];
    }

    // 联系信息验证
    if (!localQuestionnaire.contactInfo?.contactName) {
      errors.contactInfo = ['联系人姓名不能为空'];
    }

    if (!localQuestionnaire.contactInfo?.phone) {
      errors.contactInfo = [...(errors.contactInfo || []), '联系电话不能为空'];
    }

    if (!localQuestionnaire.contactInfo?.email) {
      errors.contactInfo = [...(errors.contactInfo || []), '邮箱地址不能为空'];
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [localQuestionnaire]);

  // 计算完成进度
  const calculateProgress = useCallback(() => {
    const sections = [
      'issuerBasicInfo',
      'financialStatus',
      'greenProjectInfo',
      'contactInfo',
    ];

    const completedSections = sections.filter(section => {
      const sectionData =
        localQuestionnaire[section as keyof InitialScreeningQuestionnaire];
      return sectionData && Object.keys(sectionData).length > 0;
    });

    return Math.round((completedSections.length / sections.length) * 100);
  }, [localQuestionnaire]);

  // 检查是否可以提交
  const canSubmit = useCallback(() => {
    return (
      calculateProgress() === 100 &&
      validateQuestionnaire() &&
      localQuestionnaire.status !== QuestionnaireStatus.SUBMITTED
    );
  }, [calculateProgress, validateQuestionnaire, localQuestionnaire.status]);

  // 使用useMemo缓存计算结果，避免无限循环
  const progress = React.useMemo(
    () => calculateProgress(),
    [calculateProgress]
  );
  const canSubmitValue = React.useMemo(() => canSubmit(), [canSubmit]);

  // 草稿管理功能
  const loadLatestDraft = useCallback(() => {
    try {
      const draftIndex = JSON.parse(
        localStorage.getItem('questionnaire_drafts') || '[]'
      );
      if (draftIndex.length > 0) {
        // 获取最新的草稿
        const latestDraftId = draftIndex[draftIndex.length - 1];
        const draftData = localStorage.getItem(
          `questionnaire_draft_${latestDraftId}`
        );

        if (draftData) {
          const parsedDraft = JSON.parse(draftData);
          // 确保状态类型正确
          const draftWithCorrectStatus = {
            ...parsedDraft,
            status: parsedDraft.status as QuestionnaireStatus,
          };
          setLocalQuestionnaire(draftWithCorrectStatus);
          setQuestionnaireId(parsedDraft._id);
          setLastSavedAt(
            new Date(parsedDraft.lastSavedAt || parsedDraft.updatedAt)
          );
          console.log('已加载本地草稿:', parsedDraft);
          message.info('已加载之前保存的草稿');
        }
      }
    } catch (error) {
      console.error('加载草稿失败:', error);
    }
  }, []);

  const clearAllDrafts = useCallback(() => {
    try {
      const draftIndex = JSON.parse(
        localStorage.getItem('questionnaire_drafts') || '[]'
      );
      draftIndex.forEach((draftId: string) => {
        localStorage.removeItem(`questionnaire_draft_${draftId}`);
      });
      localStorage.removeItem('questionnaire_drafts');
      console.log('已清除所有草稿');
      message.success('已清除所有本地草稿');
    } catch (error) {
      console.error('清除草稿失败:', error);
    }
  }, []);

  // 初始化时尝试加载草稿或设置默认值
  React.useEffect(() => {
    if (!questionnaireId && !initialQuestionnaireId) {
      // 创建新问卷时，直接使用默认空白数据，不加载旧草稿
      // 这样可以避免新问卷带入旧数据的问题（Bug #15）
      console.log('创建新问卷，使用默认空白数据');
      setLocalQuestionnaire(DEFAULT_QUESTIONNAIRE);
    } else if (Object.keys(localQuestionnaire).length === 0) {
      // 如果有ID但本地数据为空，也设置默认值
      setLocalQuestionnaire(DEFAULT_QUESTIONNAIRE);
    }
  }, [questionnaireId, initialQuestionnaireId]); // 移除loadLatestDraft依赖

  return {
    // 数据状态
    questionnaire: localQuestionnaire,
    isLoading: isLoadingQuestionnaire,
    hasUnsavedChanges,
    lastSavedAt,
    aiScore,
    validationErrors,

    // 计算属性
    progress,
    canSubmit: canSubmitValue,

    // 操作方法
    updateQuestionnaire: updateQuestionnaireData,
    createQuestionnaire: handleCreateQuestionnaire,
    updateQuestionnaireRemote: handleUpdateQuestionnaire,
    saveDraft: handleSaveDraft,
    submitQuestionnaire: handleSubmitQuestionnaire,
    calculateAIScore: handleCalculateAIScore,
    validateQuestionnaire,
    refetchQuestionnaire,

    // 草稿管理
    loadLatestDraft,
    clearAllDrafts,

    // 加载状态
    isCreating,
    isUpdating,
    isSubmitting,
    isSavingDraft: editMode ? isUpdating : false, // 编辑模式使用API保存时显示loading，新建模式用localStorage无loading
    isCalculatingScore,
  };
};
